import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import {
  generateMessageKey,
  encryptFileWithMessageKey,
  wrapMessageKeyWithTek,
  exportMessageKey
} from "../crypto"

// Intercepts outbox form submission to encrypt files client-side using a
// random AES-256-GCM message key, then uploads them via ActiveStorage direct
// upload. The message key is:
// 1. Wrapped (ECIES) with the company's TEK public key → stored on the server
//    so the sender can re-download later.
// 2. Sent as a hidden field (raw base64url) so the server can embed it in
//    the email URL fragment (#key=...) for the recipient.
//
// The server never stores the raw message key — it only passes through
// during email generation and is discarded.
export default class extends Controller {
  static values = {
    tekPublicKey: String
  }

  connect() {
    this.submitting = false
    this.boundHandleSubmit = this.handleSubmit.bind(this)
    this.element.addEventListener("submit", this.boundHandleSubmit)

    // Prevent ActiveStorage's built-in UJS from auto-uploading — we drive
    // the DirectUpload instances ourselves after encrypting the files.
    this.fileInputs = this.element.querySelectorAll("input[data-direct-upload-url]")
    this.directUploadUrls = new Map()
    this.fileInputs.forEach(input => {
      this.directUploadUrls.set(input, input.dataset.directUploadUrl)
      input.removeAttribute("data-direct-upload-url")
    })
  }

  disconnect() {
    this.element.removeEventListener("submit", this.boundHandleSubmit)
    this.fileInputs.forEach(input => {
      const url = this.directUploadUrls.get(input)
      if (url) input.setAttribute("data-direct-upload-url", url)
    })
  }

  async handleSubmit(event) {
    // Guard: after encrypting we call requestSubmit() which re-fires this
    // handler — let the re-submission pass through to Turbo.
    if (this.submitting) return
    if (event.defaultPrevented) return

    if (!this.hasTekPublicKeyValue || !this.tekPublicKeyValue) {
      event.preventDefault()
      alert("Versleuteling is niet beschikbaar. Probeer het later opnieuw.")
      return
    }

    event.preventDefault()
    const submitButton = this.element.querySelector("[type='submit']")
    if (submitButton) submitButton.disabled = true

    try {
      // Count total files for progress tracking
      let totalFiles = 0
      for (const input of this.fileInputs) {
        if (input.files) totalFiles += input.files.length
      }

      if (totalFiles === 0) {
        if (submitButton) submitButton.disabled = false
        return
      }

      // Generate a single message key for all files in this message
      const messageKey = await generateMessageKey()

      // Dispatch upload start event
      this.dispatchProgressEvent("upload:start", { totalFiles })

      let fileIndex = 0

      for (const input of this.fileInputs) {
        const files = input.files
        if (!files || files.length === 0) continue
        const uploadUrl = this.directUploadUrls.get(input)
        if (!uploadUrl) continue

        for (const file of files) {
          // Dispatch encrypting progress
          this.dispatchProgressEvent("upload:progress", {
            progress: 0,
            fileName: file.name,
            phase: "encrypting"
          })

          const plaintext = await file.arrayBuffer()
          const result = await encryptFileWithMessageKey(
            messageKey,
            plaintext
          )

          // Build a File from the ciphertext so ActiveStorage computes the
          // correct checksum and byte_size from the encrypted payload.
          // Use a random filename to avoid leaking original name and to ensure
          // Rails doesn't infer content type from the extension.
          const encryptedFile = new File(
            [result.ciphertext],
            `${crypto.randomUUID()}.enc`,
            { type: "application/octet-stream" }
          )

          // Upload the encrypted file via ActiveStorage DirectUpload
          const blob = await this.directUpload(encryptedFile, uploadUrl, file.name)

          // Dispatch file complete event
          this.dispatchProgressEvent("upload:file-complete", { fileName: file.name })

          // Insert a hidden field with the blob signed_id for Rails
          this.addHiddenInput(input.name, blob.signed_id)

          // Insert per-file encryption metadata
          const metaFields = {
            iv: result.iv,
            original_content_type: file.type || "application/octet-stream",
            original_byte_size: file.size,
            original_filename: file.name
          }
          for (const [key, value] of Object.entries(metaFields)) {
            this.addHiddenInput(`encryption_metadata[${fileIndex}][${key}]`, value)
          }
          fileIndex++
        }

        // Disable the file input so the raw files are not sent with the form
        input.disabled = true
      }

      // Wrap the message key with TEK public key (ECIES) for sender re-access
      const encryptedMessageKey = await wrapMessageKeyWithTek(
        this.tekPublicKeyValue,
        messageKey
      )

      // Export raw message key for the email URL fragment
      const rawMessageKey = await exportMessageKey(messageKey)

      // Add encryption fields as hidden inputs
      this.addHiddenInput("outgoing_message[encrypted]", "true")
      this.addHiddenInput("outgoing_message[encrypted_message_key]", encryptedMessageKey)
      this.addHiddenInput("raw_message_key", rawMessageKey)

      // Dispatch upload complete event
      this.dispatchProgressEvent("upload:complete", {})

      // Submit the form normally — Turbo handles redirects & streams
      this.submitting = true
      this.element.requestSubmit()
    } catch (error) {
      this.submitting = false
      console.error("Encryption failed:", error)
      this.dispatchProgressEvent("upload:error", { error: error.message })
      alert("Versleuteling mislukt. Probeer het opnieuw.")
      if (submitButton) submitButton.disabled = false
    }
  }

  // Returns a promise that resolves to the blob JSON from the server
  directUpload(file, url, originalFileName) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, url, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", event => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100)
              this.dispatchProgressEvent("upload:progress", {
                progress,
                fileName: originalFileName,
                phase: "uploading"
              })
            }
          })
        }
      })
      upload.create((error, blob) => {
        if (error) reject(error)
        else resolve(blob)
      })
    })
  }

  // DirectUpload delegate callbacks for progress (optional)
  directUploadWillStoreFileWithXHR(request) {
    // Handled inline in directUpload method
  }

  dispatchProgressEvent(eventName, detail) {
    // Dispatch on the first file input so it bubbles up through the file upload container
    const target = this.fileInputs[0] || this.element
    target.dispatchEvent(new CustomEvent(eventName, {
      bubbles: true,
      detail
    }))
  }

  addHiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    this.element.appendChild(input)
  }
}
